/**
 * Demo patient list for the sandbox harness dropdown.
 *
 * Derived from Elasticsearch `images_v1` hits (`_source.patient`): each row
 * carries the `patient_id` and `office_id` strings used by `buildUrl` /
 * preload context (Second Opinion disambiguation, PI deep links, etc.).
 *
 * Deduplicated by `patient_id` (one option per patient; the index may return
 * multiple hits per patient for different `image_created_date` values).
 */

export interface DemoPatientOption {
  /** `_source.patient.patient_id` */
  patientId: string;
  /** `_source.patient.office_id` */
  officeId: string;
  /** `_source.patient.full_name` — shown in the dropdown. */
  fullName: string;
}

/**
 * Unique patients from a sample `hits.hits[]` (pearlsandbox / office 1).
 * Sorted by `fullName` for the dropdown.
 */
export const DEMO_PATIENT_OPTIONS: readonly DemoPatientOption[] = [
  { patientId: '12156372', officeId: '1', fullName: 'Ana Bennet' },
  { patientId: '12149629', officeId: '1', fullName: 'Aron Powlowski' },
  { patientId: '74574306', officeId: '1', fullName: 'Benjamin Draper' },
  { patientId: '12151990', officeId: '1', fullName: 'Charles Shaw' },
  { patientId: '11623', officeId: '1', fullName: 'Chastity Feest' },
  { patientId: '12159911', officeId: '1', fullName: 'Cordelia Fay' },
  { patientId: '12260', officeId: '1', fullName: 'Corinne Krajcik' },
  { patientId: '5943', officeId: '1', fullName: 'Deirdre McKenzie' },
  { patientId: '13471', officeId: '1', fullName: 'Dominic Witting' },
  { patientId: '19254', officeId: '1', fullName: 'Doug Tomako' },
  { patientId: '12161176', officeId: '1', fullName: 'Eldora Kerluke' },
  { patientId: '036982', officeId: '1', fullName: 'Ethan Garcia' },
  { patientId: '13491', officeId: '1', fullName: 'Eugene Bahringer' },
  { patientId: '12160880', officeId: '1', fullName: 'Fernando Kovacek' },
  { patientId: '74308998', officeId: '1', fullName: 'Haley Marlowe' },
  { patientId: '13472', officeId: '1', fullName: 'Irvin Stroman' },
  { patientId: '11576562', officeId: '1', fullName: 'Jackson Reed' },
  { patientId: '11856362', officeId: '1', fullName: 'James Clark' },
  { patientId: '23669', officeId: '1', fullName: 'Jessica Branson' },
  { patientId: '27215', officeId: '1', fullName: 'Kate Stewart' },
  { patientId: '12147792', officeId: '1', fullName: 'Kathryn Hirthe' },
  { patientId: '12131641', officeId: '1', fullName: 'Kirby Satterfield' },
  { patientId: '12150004', officeId: '1', fullName: 'Latricia Lang' },
  { patientId: '13434', officeId: '1', fullName: 'Leilani Jones' },
  { patientId: '7705', officeId: '1', fullName: 'Liam Patel' },
  { patientId: '12157181', officeId: '1', fullName: 'Lindsay Cornell' },
  { patientId: '19781', officeId: '1', fullName: "Maya O'Connor" },
  { patientId: '10445', officeId: '1', fullName: 'Mindy Littel' },
  { patientId: '12151595', officeId: '1', fullName: 'Odette Maggio' },
  { patientId: '13492', officeId: '1', fullName: 'Pilar OHara' },
  { patientId: '12342', officeId: '1', fullName: 'Raven Labadie' },
  { patientId: '13479', officeId: '1', fullName: 'Rosalia Medhurst' },
  { patientId: '032969', officeId: '1', fullName: 'Samuel Chen' },
  { patientId: '13390', officeId: '1', fullName: 'Svetlana Grimes' },
  { patientId: '12144506', officeId: '1', fullName: 'Tom Koepp' },
  { patientId: '12161300', officeId: '1', fullName: 'Tory Kling' },
  { patientId: '12159802', officeId: '1', fullName: 'Tyson Kuhic' },
  { patientId: '7015', officeId: '1', fullName: 'Vennie Morar' },
  { patientId: '9638', officeId: '1', fullName: 'Wilda Blick' },
  { patientId: '13455', officeId: '1', fullName: 'Yulanda Powlowski' },
  { patientId: '2550', officeId: '1', fullName: 'Zoe Alvarez' },
];

/**
 * Looks up a demo patient by id — used by static-patient pages (Dexis, the
 * Dentrix chart) that carry a patient id in the URL or body dataset instead
 * of a dropdown.
 *
 * @param patientId - `_source.patient.patient_id` to find
 * @returns The matching row, or `null` when unknown/empty
 */
export function findDemoPatient(patientId: string): DemoPatientOption | null {
  if (!patientId) return null;
  return DEMO_PATIENT_OPTIONS.find((row) => row.patientId === patientId) ?? null;
}

/**
 * Resolves the fixed patient for pages without a `#patient-select` dropdown:
 * `?patient=<id>` in the URL (Dentrix chart deep links) wins over the page's
 * `<body data-demo-patient-id>` pin (the static Dexis page).
 *
 * @param search - `window.location.search` of the demo page
 * @param bodyPatientId - `document.body.dataset.demoPatientId`, if any
 * @returns The matching row, or `null` when neither source names a known patient
 */
export function resolveStaticDemoPatient(
  search: string,
  bodyPatientId: string | undefined,
): DemoPatientOption | null {
  const fromQuery = new URLSearchParams(search).get('patient');
  return findDemoPatient((fromQuery || bodyPatientId || '').trim());
}
